class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.heading = this.querySelector('.cart-notification__heading');
    this.headingDefaultHTML = this.heading?.innerHTML || '';
    this.productContainer = document.getElementById('cart-notification-product');
    this.cartButton = document.getElementById('cart-notification-button');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.moneyFormatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    });

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.bindHeaderCartPreview();
  }

  open({ trap = true } = {}) {
    clearTimeout(this.closeTimer);
    this.notification.classList.add('animate', 'active');

    if (trap) {
      this.notification.addEventListener(
        'transitionend',
        () => {
          this.notification.focus();
          trapFocus(this.notification);
        },
        { once: true }
      );
    }

    document.body.addEventListener('click', this.onBodyClick);
    this.closeTimer = setTimeout(() => this.close(), 4000);
  }

  close() {
    clearTimeout(this.closeTimer);
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key || parsedState.items?.[0]?.key;
    if (!this.cartItemKey || !parsedState.sections) return;

    this.notification.classList.remove('cart-notification--summary');
    if (this.heading) this.heading.innerHTML = this.headingDefaultHTML;

    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      const html = parsedState.sections[section.id];
      if (container && html) container.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  bindHeaderCartPreview() {
    const cartIcons = document.querySelectorAll('#cart-icon-bubble, [data-cart-preview-trigger]');
    if (!cartIcons.length) return;

    const showPreview = () => {
      if (window.matchMedia('(hover: none)').matches || window.innerWidth < 990) return;
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.showCartSummary(), 120);
    };

    const showTapPreview = (event) => {
      if (!window.matchMedia('(hover: none)').matches && window.innerWidth >= 990) return;
      event.preventDefault();
      clearTimeout(this.previewTimer);
      this.showCartSummary();
    };

    const cancelPreview = () => clearTimeout(this.previewTimer);

    cartIcons.forEach((cartIcon) => {
      cartIcon.addEventListener('mouseenter', showPreview);
      cartIcon.addEventListener('focus', showPreview);
      cartIcon.addEventListener('mouseleave', cancelPreview);
      cartIcon.addEventListener('click', showTapPreview);
    });
  }

  showCartSummary() {
    const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';

    fetch(cartUrl)
      .then((response) => response.json())
      .then((cart) => {
        const itemCount = Number(cart.item_count || 0);
        const total = Number(cart.total_price || cart.items_subtotal_price || 0);
        const firstItem = cart.items?.[0];

        this.notification.classList.add('cart-notification--summary');
        if (this.heading) this.heading.textContent = 'Resumen del carrito';
        if (this.cartButton) this.cartButton.textContent = `Ver carrito - ${this.formatMoney(total)}`;

        if (this.productContainer) {
          const image = firstItem?.image
            ? `<img src="${firstItem.image}" alt="${this.escapeHtml(firstItem.product_title || 'Producto')}" loading="lazy" width="70" height="70">`
            : '';
          const summaryText = itemCount === 1 ? '1 producto en tu carrito' : `${itemCount} productos en tu carrito`;
          const title = firstItem?.product_title || 'Tu pedido mayorista';

          this.productContainer.innerHTML = `
            <div class="cart-notification-summary">
              ${image ? `<div class="cart-notification-summary__image">${image}</div>` : ''}
              <div class="cart-notification-summary__content">
                <strong>${this.escapeHtml(summaryText)}</strong>
                <span>${this.escapeHtml(title)}</span>
                <em>Total: ${this.formatMoney(total)}</em>
              </div>
            </div>
          `;
        }

        this.open({ trap: false });
      })
      .catch(() => {});
  }

  formatMoney(cents) {
    return this.moneyFormatter.format(Math.max(0, Number(cents || 0)) / 100).replace(/\s/g, ' ');
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[character];
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'minimum-progress-cart-icon',
        section: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
